import React from "react";
import * as XLSX from "xlsx";
import { TbFile } from "react-icons/tb";
import { Button } from "@nextui-org/react";
import { formatSecondHandProduct } from "../../utils/secondHandProduct";

const ExportExcel = ({ data, date }) => {
  const handleExport = () => {
    // Helpers
    const formatValue = (val) => (val > 0 ? val : 0);
    const displayValue = (val) => (val > 0 ? val : "-");

    // ---- Orders ----
    let orders = data?.orders?.map((doc, i) => {
      const ctipin = doc?.products1?.map((d) => d?.ctpin).join(", ");
      let cash = 0,
        card = 0,
        cashfree = 0,
        ma = 0,
        online = 0,
        udhar = 0,
        replace = 0,
        exchange = 0;

      if (doc?.payment_type === "Other") {
        cash = formatValue(doc?.paid_struc?.cash);
        card = formatValue(doc?.paid_struc?.card);
        cashfree = formatValue(doc?.paid_struc?.cashfree);
        ma = formatValue(doc?.paid_struc?.ma);
        online = formatValue(doc?.paid_struc?.bank);
        udhar = formatValue(doc?.paid_struc?.loaned);
        replace = formatValue(doc?.paid_struc?.replace);
        exchange = formatValue(doc?.paid_struc?.exchange);
      } else if (doc?.payment_type === "Cash") {
        cash = formatValue(doc?.total);
      } else if (doc?.payment_type === "Card") {
        card = formatValue(doc?.total);
      } else if (doc?.payment_type === "Cashfree") {
        cashfree = formatValue(doc?.total);
      } else if (doc?.payment_type === "MA") {
        ma = formatValue(doc?.total);
      } else if (doc?.payment_type === "Online") {
        online = formatValue(doc?.total);
      } else if (doc?.payment_type === "Udhar") {
        udhar = formatValue(doc?.total);
      }

      return {
        Sr: i + 1,
        Time: new Date(doc?.order_date).toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata",
        }),
        Products: doc?.products1
          ?.map((d) => `${d?.name} ${d?.desc}${(d.is2H || doc.is2H) ? " (2H)" : (d.is3H || doc.is3H) ? " (3H)" : ""}`)
          .join(", "),
        Name: doc?.billName,
        Mobile: doc?.customer?.mobile,
        CTIPIN: ctipin,
        Customer: doc?.customer?.name,
        Cash: displayValue(cash),
        Card: displayValue(card),
        Cashfree: displayValue(cashfree),
        Ma: displayValue(ma),
        Online: displayValue(online),
        Udhar: displayValue(udhar),
        Replace: displayValue(replace),
        Exchange: displayValue(exchange),
        Total: displayValue(formatValue(doc?.total)),
        Type: "Order",
      };
    });

    // ---- Totals ----
    let cash = orders.reduce(
      (a, b) => a + (b.Cash !== "-" ? Number(b.Cash) : 0),
      0,
    );
    let card = orders.reduce(
      (a, b) => a + (b.Card !== "-" ? Number(b.Card) : 0),
      0,
    );
    let cashfree = orders.reduce(
      (a, b) => a + (b.Cashfree !== "-" ? Number(b.Cashfree) : 0),
      0,
    );
    let online = orders.reduce(
      (a, b) => a + (b.Online !== "-" ? Number(b.Online) : 0),
      0,
    );
    let ma = orders.reduce((a, b) => a + (b.Ma !== "-" ? Number(b.Ma) : 0), 0);
    let udhar = orders.reduce(
      (a, b) => a + (b.Udhar !== "-" ? Number(b.Udhar) : 0),
      0,
    );
    let replace = orders.reduce(
      (a, b) => a + (b.Replace !== "-" ? Number(b.Replace) : 0),
      0,
    );
    let exchange = orders.reduce(
      (a, b) => a + (b.Exchange !== "-" ? Number(b.Exchange) : 0),
      0,
    );
    let total =
      cash + card + cashfree + online + ma + udhar + replace + exchange;

    // ---- Purchases ----
    let purchases = data?.purchases?.map((doc, i) => {
      const amt = formatValue(doc?.purchaseAmount);
      const isCash = doc?.paymentMethod === "Cash";
      const isUPI = doc?.paymentMethod === "UPI";
      const isCard = doc?.paymentMethod === "Card";
      const isMix = doc?.paymentMethod === "Mix";

      // Reusing Orders columns for consistency where possible
      // Orders: Cash, Card, Cashfree, Ma, Online, Udhar, Replace, Exchange
      // Purchases: Cash, UPI (map to Online?), Card, Mix (map to Cashfree/Ma or keep separate?)
      // Let's assume standard columns: Cash, Card, Online (UPI). Mix might need a generic or specific col.
      // Since ExcelPrint helper has specific columns, let's map:
      // UPI -> Online
      // Mix -> Cashfree (just as a placeholder column if unused, or add new columns to Excel if structure allows)
      // The current Excel structure allows any keys. Let's use specific keys for Purchase if we want to be explicit, but usually reports share columns.
      // However, the requested format is Cash, UPI, Card, Mix.
      // Existing excel_print.js has fixed keys in the Orders map.
      // Let's map to existing columns for 'Cash' and 'Card'. 'Online' can be UPI.
      // 'Mix' can be 'Ma' or 'Cashfree' or we can add new keys.
      // If I add new keys, they will show up as new columns in Excel.

      return {
        Sr: i + 1,
        Time: new Date(doc?.created).toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata",
        }),
        Products: `${formatSecondHandProduct(doc)} - ${doc?.condition || ""}`,
        Name: doc?.name,
        Mobile: doc?.cellphone,
        CTIPIN: doc?.imei,
        Customer: "PURCHASE",
        Cash: isCash ? displayValue(amt) : "-",
        Card: isCard ? displayValue(amt) : "-",
        Cashfree: isMix ? displayValue(amt) : "-", // Using Cashfree col for Mix
        Ma: "-",
        Online: isUPI ? displayValue(amt) : "-", // Using Online col for UPI
        Udhar: "-",
        Replace: "-",
        Exchange: "-",
        Total: displayValue(amt),
        Type: "Purchase",
      };
    });

    // Calculate Purchase Totals
    let purchaseCash = purchases?.reduce(
      (a, b) => a + (b.Cash !== "-" ? Number(b.Cash) : 0),
      0,
    );
    let purchaseCard = purchases?.reduce(
      (a, b) => a + (b.Card !== "-" ? Number(b.Card) : 0),
      0,
    );
    let purchaseMix = purchases?.reduce(
      (a, b) => a + (b.Cashfree !== "-" ? Number(b.Cashfree) : 0),
      0,
    );
    let purchaseUPI = purchases?.reduce(
      (a, b) => a + (b.Online !== "-" ? Number(b.Online) : 0),
      0,
    );
    let purchaseTotal = purchases?.reduce(
      (a, b) => a + (b.Total !== "-" ? Number(b.Total) : 0),
      0,
    );

    // Merge
    let allRows = [...orders, ...purchases];

    // Add totals row
    allRows.push({
      Sr: "",
      Time: "",
      Products: "---- Sales Totals ----",
      Name: "",
      Mobile: "",
      CTIPIN: "",
      Customer: "",
      Cash: displayValue(cash),
      Card: displayValue(card),
      Cashfree: displayValue(cashfree),
      Ma: displayValue(ma),
      Online: displayValue(online),
      Udhar: displayValue(udhar),
      Replace: displayValue(replace),
      Exchange: displayValue(exchange),
      Total: displayValue(total), // Sales Total
      Type: "Sales Total",
    });

    allRows.push({
      Sr: "",
      Time: "",
      Products: "---- Purchases Totals ----",
      Name: "",
      Mobile: "",
      CTIPIN: "",
      Customer: "",
      Cash: displayValue(purchaseCash),
      Card: displayValue(purchaseCard),
      Cashfree: displayValue(purchaseMix) + " (Mix)",
      Ma: "-",
      Online: displayValue(purchaseUPI) + " (UPI)",
      Udhar: "-",
      Replace: "-",
      Exchange: "-",
      Total: displayValue(purchaseTotal),
      Type: "Purchase Total",
    });

    // ---- Excel ----
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(allRows);

    XLSX.utils.book_append_sheet(wb, ws, "Report");

    XLSX.writeFile(wb, `Sales_Report_${date || "report"}.xlsx`);
  };

  return (
    <Button
      icon={<TbFile />}
      onClick={handleExport}
      color={"success"}
      auto
      size={"sm"}
    />
  );
};

export default ExportExcel;
